import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';

import { AppState } from 'services/types';
import { ColumnWidthTableId } from 'services/columnWidths/types';
import {
    VirtualizedTableColumns,
    VirtualizedTableItems,
    VirtualizedTableItemKey,
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth
} from 'services/virtualizedTable/types';
import {
    VirtualizedTableOnItemValueChange,
    VirtualizedTableOnItemsValueChange
} from 'components/VirtualizedTable/types';
import VirtualizedTable from 'components/VirtualizedTable';
import { setColumnWidth } from 'services/columnWidths/actions';
import { updateQueryString } from 'services/router/actions';
import {
    updateHarvestEventValue,
    updateHarvestEventsValue
} from 'services/harvestEvents/actions';
import {
    selectHarvestEventsTableColumns,
    selectHarvestEventsArrayItems,
    selectIsWritable
} from './selectors';

export interface OwnProps {
    tableId: ColumnWidthTableId;
}

interface ConnectedProps {
    columns: VirtualizedTableColumns;
    items: VirtualizedTableItems;
    isWritable: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    updateQueryString: typeof updateQueryString;
    updateHarvestEventValue: typeof updateHarvestEventValue;
    updateHarvestEventsValue: typeof updateHarvestEventsValue;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class HarvestEventsTableComponent extends React.PureComponent<Props> {
    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;

        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleItemValueChange: VirtualizedTableOnItemValueChange = (
        itemKey,
        columnKey,
        itemValue
    ) => {
        this.props.updateHarvestEventValue({
            harvestEventUuid: itemKey,
            field: columnKey,
            value: itemValue
        });
    };

    handleOnExpand = (key: VirtualizedTableItemKey): void => {
        this.props.updateQueryString({
            seasonalEventModal: true,
            selectedEvent: key
        });
    };

    handleItemsValueChange: VirtualizedTableOnItemsValueChange = (
        itemKeys,
        columnKey,
        itemValue
    ) => {
        this.props.updateHarvestEventsValue({
            harvestEventUuids: itemKeys,
            field: columnKey,
            value: itemValue
        });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, columns, items, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                label="Harvest Event"
                labelPlural="Harvest Events"
                onColumnResized={this.handleColumnResized}
                onItemValueChange={this.handleItemValueChange}
                onItemsValueChange={this.handleItemsValueChange}
                onExpand={isWritable ? this.handleOnExpand : undefined}
                editable={isWritable}
                selectable={isWritable}
                enableBulkEditing={isWritable}
                enableFooter
            />
        );
    };

    render(): React.ReactElement {
        return (
            <React.Fragment>
                <AutoSizer>
                    {(size: Size): React.ReactElement =>
                        this.renderVirtualizedTable(size)
                    }
                </AutoSizer>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    columns: selectHarvestEventsTableColumns,
    items: selectHarvestEventsArrayItems,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    updateQueryString,
    setColumnWidth,
    updateHarvestEventValue,
    updateHarvestEventsValue
})(HarvestEventsTableComponent);
